/**
 * Kai Voice — the widget's transport to OpenAI GPT-Live, on the browser's own
 * WebRTC stack (no SDK). Microphone → media track → GPT-Live; GPT-Live speech ←
 * remote track → an <audio> element; JSON events ride the "oai-events" data
 * channel (session.started, transcripts, delegations, session.closed). The SDP
 * exchange goes through our API, which creates the session and attaches the
 * server-side sideband that answers the delegations.
 */

import { getCallAudioContext } from "./KaiVoiceTones";

export interface LiveEvent {
  type: string;
  [key: string]: any;
}

export interface KaiVoiceConnectionHandlers {
  /** Every JSON event GPT-Live sends the browser. */
  onEvent: (event: LiveEvent) => void;
  /** The peer connection died (network) or was closed remotely without a session.closed. */
  onConnectionLost: () => void;
}

// GPT-Live does not send an "end of speech" event; the speaking indicator
// therefore follows the output transcript and decays after this silence.
export const SPEAKING_DECAY_MS = 1200;
// Wait for ICE gathering before shipping the offer (better connectivity);
// trickle ICE means the offer is still valid if this runs out.
const ICE_GATHERING_WAIT_MS = 1000;
const LEVEL_SAMPLES = 512;
// Jitter headroom for GPT-Live's audio. The browser's adaptive jitter buffer
// starts near zero and grows on the first hiccups, which shows up as a
// dropout inside the first sentence (the greeting). Asking for a small fixed
// target up front trades ~100ms of latency for a clean start.
const JITTER_BUFFER_TARGET_MS = 120;

const getUserMicrophone = (): Promise<MediaStream> =>
  navigator.mediaDevices.getUserMedia({
    audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
  });

const waitForIceGathering = (pc: RTCPeerConnection): Promise<void> =>
  new Promise((resolve) => {
    if (pc.iceGatheringState === "complete") {
      resolve();
      return;
    }
    const timer = setTimeout(done, ICE_GATHERING_WAIT_MS);
    function done() {
      clearTimeout(timer);
      pc.removeEventListener("icegatheringstatechange", check);
      resolve();
    }
    function check() {
      if (pc.iceGatheringState === "complete") done();
    }
    pc.addEventListener("icegatheringstatechange", check);
  });

export class KaiVoiceConnection {
  private readonly pc: RTCPeerConnection;
  private readonly channel: RTCDataChannel;
  private readonly microphone: MediaStream;
  private readonly audio: HTMLAudioElement;
  private readonly handlers: KaiVoiceConnectionHandlers;
  private audioContext: AudioContext | null = null;
  private meterSources: MediaStreamAudioSourceNode[] = [];
  private micAnalyser: AnalyserNode | null = null;
  private remoteAnalyser: AnalyserNode | null = null;
  private levelBuffer: Uint8Array | null = null;
  private started = false;
  private readyWaiters: Array<() => void> = [];
  private playing = false;
  private playingWaiters: Array<() => void> = [];
  private closed = false;

  private constructor(
    pc: RTCPeerConnection,
    channel: RTCDataChannel,
    microphone: MediaStream,
    audio: HTMLAudioElement,
    handlers: KaiVoiceConnectionHandlers,
  ) {
    this.pc = pc;
    this.channel = channel;
    this.microphone = microphone;
    this.audio = audio;
    this.handlers = handlers;
  }

  /**
   * Open the call: microphone, peer connection, SDP exchange through
   * `exchangeSdp` (our POST /shared/calls), remote description. Resolves
   * once the answer is applied; `waitForStarted` then covers the data channel.
   */
  static async open(args: {
    exchangeSdp: (offer: string) => Promise<string>;
    handlers: KaiVoiceConnectionHandlers;
  }): Promise<KaiVoiceConnection> {
    const microphone = await getUserMicrophone();
    const pc = new RTCPeerConnection();
    const channel = pc.createDataChannel("oai-events");
    const audio = document.createElement("audio");
    audio.autoplay = true;
    (audio as any).playsInline = true;

    const connection = new KaiVoiceConnection(pc, channel, microphone, audio, args.handlers);
    connection.bind();
    for (const track of microphone.getAudioTracks()) {
      pc.addTrack(track, microphone);
    }

    try {
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      await waitForIceGathering(pc);
      const answer = await args.exchangeSdp(pc.localDescription?.sdp ?? offer.sdp ?? "");
      await pc.setRemoteDescription({ type: "answer", sdp: answer });
    } catch (err) {
      connection.close();
      throw err;
    }
    return connection;
  }

  private bind(): void {
    this.audio.addEventListener("playing", () => {
      this.playing = true;
      this.playingWaiters.splice(0).forEach((resolve) => resolve());
    });
    this.pc.ontrack = (event) => {
      const stream = event.streams[0] ?? new MediaStream([event.track]);
      try {
        // Standard name first, then the older Chrome spelling (seconds).
        const receiver = event.receiver as any;
        if (receiver && "jitterBufferTarget" in receiver) receiver.jitterBufferTarget = JITTER_BUFFER_TARGET_MS;
        else if (receiver && "playoutDelayHint" in receiver) receiver.playoutDelayHint = JITTER_BUFFER_TARGET_MS / 1000;
      } catch (_) {}
      this.audio.srcObject = stream;
      this.audio.play().catch(() => {});
      this.attachAnalysers(stream);
    };
    this.pc.onconnectionstatechange = () => {
      const state = this.pc.connectionState;
      if (this.closed) return;
      if (state === "failed" || state === "closed" || state === "disconnected") {
        this.handlers.onConnectionLost();
      }
    };
    this.channel.onmessage = (message) => {
      let event: LiveEvent;
      try {
        event = JSON.parse(message.data);
      } catch (_) {
        return;
      }
      if (!event?.type) return;
      if (event.type === "session.started") {
        this.started = true;
        this.readyWaiters.splice(0).forEach((resolve) => resolve());
      }
      this.handlers.onEvent(event);
    };
    // The session already exists (our API created it before the answer came
    // back), so an open channel is enough to talk to it; `session.started` is
    // the confirmation when GPT-Live replays it onto the channel.
    this.channel.onopen = () => {
      this.readyWaiters.splice(0).forEach((resolve) => resolve());
    };
  }

  get isReady(): boolean {
    return this.started || this.channel.readyState === "open";
  }

  /** Resolves once events can be sent (data channel open or `session.started` seen), false after `timeoutMs`. */
  waitForReady(timeoutMs: number): Promise<boolean> {
    if (this.isReady) return Promise.resolve(true);
    return new Promise((resolve) => {
      const timer = setTimeout(() => resolve(this.isReady), timeoutMs);
      this.readyWaiters.push(() => {
        clearTimeout(timer);
        resolve(true);
      });
    });
  }

  /**
   * Resolves once the remote audio is actually being rendered (the <audio>
   * element's `playing`), false after `timeoutMs`. Speech sent before this
   * lands in a cold playout pipeline and its first syllables get mangled.
   */
  waitForPlayback(timeoutMs: number): Promise<boolean> {
    if (this.playing) return Promise.resolve(true);
    return new Promise((resolve) => {
      const timer = setTimeout(() => resolve(this.playing), timeoutMs);
      this.playingWaiters.push(() => {
        clearTimeout(timer);
        resolve(true);
      });
    });
  }

  send(event: LiveEvent): void {
    if (this.closed || this.channel.readyState !== "open") return;
    this.channel.send(JSON.stringify(event));
  }

  /** The customer's microphone; GPT-Live's input itself (on hold, before the greeting) is the server's to mute. */
  setMuted(muted: boolean): void {
    for (const track of this.microphone.getAudioTracks()) {
      track.enabled = !muted;
    }
  }

  /** 0..1 — the louder of microphone and Kai's voice, for the waveform. */
  getLevel(): number {
    const analysers = [this.micAnalyser, this.remoteAnalyser].filter(Boolean) as AnalyserNode[];
    if (analysers.length === 0 || !this.levelBuffer) return 0;
    let peak = 0;
    for (const analyser of analysers) {
      peak = Math.max(peak, this.rms(analyser, this.levelBuffer));
    }
    return Math.min(peak * 3, 1);
  }

  /** RMS of Kai's voice alone (about 0 while it is silent), or null where it cannot be read. */
  getAgentLevel(): number | null {
    if (!this.remoteAnalyser || !this.levelBuffer || this.audioContext?.state !== "running") return null;
    return this.rms(this.remoteAnalyser, this.levelBuffer);
  }

  private rms(analyser: AnalyserNode, buffer: Uint8Array): number {
    analyser.getByteTimeDomainData(buffer);
    let sum = 0;
    for (let i = 0; i < buffer.length; i++) {
      const centered = (buffer[i] - 128) / 128;
      sum += centered * centered;
    }
    return Math.sqrt(sum / buffer.length);
  }

  private attachAnalysers(remote: MediaStream): void {
    try {
      // The call's context, started in the click: Safari keeps one created here (after it) suspended.
      this.audioContext = getCallAudioContext();
      if (!this.audioContext) return;
      this.levelBuffer = new Uint8Array(LEVEL_SAMPLES);
      const make = (stream: MediaStream): AnalyserNode => {
        const analyser = this.audioContext!.createAnalyser();
        analyser.fftSize = LEVEL_SAMPLES;
        const source = this.audioContext!.createMediaStreamSource(stream);
        source.connect(analyser);
        this.meterSources.push(source);
        return analyser;
      };
      this.micAnalyser = this.micAnalyser ?? make(this.microphone);
      this.remoteAnalyser = make(remote);
    } catch (_) {
      // Level metering is cosmetic — the call works without it.
    }
  }

  /**
   * Hang up: ask GPT-Live to close the session (the server finalizes on its
   * `session.closed`), then tear the media down.
   */
  close(): void {
    if (this.closed) return;
    this.send({ type: "session.close" });
    this.closed = true;
    try {
      this.microphone.getTracks().forEach((track) => track.stop());
    } catch (_) {}
    try {
      this.pc.close();
    } catch (_) {}
    try {
      this.audio.pause();
      this.audio.srcObject = null;
    } catch (_) {}
    // The context is the call's (tones, hold music): only this connection's meters go.
    this.meterSources.forEach((source) => {
      try {
        source.disconnect();
      } catch (_) {}
    });
    this.meterSources = [];
    this.audioContext = null;
    this.micAnalyser = null;
    this.remoteAnalyser = null;
  }
}
